import React, { useState } from 'react';

const LandingPage = ({ transactions = [], transactionTypes = [], onSelectTransaction, onCreateNew }) => {
  const [viewMode, setViewMode] = useState('card');
  const [filters, setFilters] = useState({
    searchTerm: '',
    txnType: 'All',
    startDate: '',
    endDate: ''
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const resetFilters = () => {
    setFilters({ searchTerm: '', txnType: 'All', startDate: '', endDate: '' });
  };

  const filteredTransactions = (transactions || [])
    .filter(t => filters.txnType === 'All' || t.txnTypeName === filters.txnType)
    .filter(t => {
      if (!filters.startDate || !filters.endDate) return true;
      const docDate = new Date(t.docDate);
      return docDate >= new Date(filters.startDate) && docDate <= new Date(filters.endDate);
    })
    .filter(t =>
      t.docNumber.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
      t.partyName.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
      t.mode.toLowerCase().includes(filters.searchTerm.toLowerCase())
    );

  const recordCount = filteredTransactions.length;
  const totalValue = filteredTransactions.reduce((sum, txn) => sum + txn.netTotal, 0);

  const StatusBadge = ({ status, mode }) => {
    let text = mode;
    let color = 'bg-yellow-100 text-yellow-800';
    if (mode === 'Finished') {
      if (status.verified) { text = 'Verified'; color = 'bg-green-100 text-green-800'; }
      else if (status.checked) { text = 'Checked'; color = 'bg-blue-100 text-blue-800'; }
    }
    return <span className={`px-2 py-1 text-xs font-medium rounded-full ${color}`}>{text}</span>;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
        <h1 className="text-3xl font-bold text-gray-800">Transactions</h1>
        <button onClick={onCreateNew} className="w-full px-6 py-2 font-semibold text-white bg-indigo-600 rounded-md shadow-md md:w-auto hover:bg-indigo-700">
          Create New Transaction
        </button>
      </div>

      <div className="p-4 space-y-4 bg-white border rounded-lg shadow-sm">
        <div className="flex items-center justify-between">
          <button onClick={() => setIsFilterOpen(!isFilterOpen)} className="flex items-center gap-2 font-semibold text-indigo-600">
            <span>Show Filters</span>
            <svg className={`w-5 h-5 transform transition-transform ${isFilterOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </button>
          <div className="flex items-center gap-2 p-1 bg-gray-200 rounded-lg">
            <button onClick={() => setViewMode('card')} className={`px-3 py-1 rounded-md text-sm transition-colors ${viewMode === 'card' ? 'bg-white text-indigo-600 shadow' : 'bg-transparent text-gray-600'}`}>
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor"><path d="M5 3a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V5a2 2 0 00-2-2H5zm0 2h10v10H5V5z" /></svg>
            </button>
            <button onClick={() => setViewMode('table')} className={`px-3 py-1 rounded-md text-sm transition-colors ${viewMode === 'table' ? 'bg-white text-indigo-600 shadow' : 'bg-transparent text-gray-600'}`}>
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" /></svg>
            </button>
          </div>
        </div>

        {isFilterOpen && (
          <div className="pt-4 space-y-4 border-t">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              <input type="text" name="searchTerm" placeholder="Search..." value={filters.searchTerm} onChange={handleFilterChange} className="w-full p-2 border border-gray-300 rounded-md" />
              <select name="txnType" value={filters.txnType} onChange={handleFilterChange} className="w-full p-2 border border-gray-300 rounded-md">
                <option value="All">All Types</option>
                {(transactionTypes || []).map(type => <option key={type.id} value={type.name}>{type.name}</option>)}
              </select>
              <input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} className="w-full p-2 border border-gray-300 rounded-md" />
              <input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} className="w-full p-2 border border-gray-300 rounded-md" />
            </div>
            <div className="flex justify-end">
              <button onClick={resetFilters} className="px-4 py-2 bg-gray-200 rounded-md">Reset</button>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-around p-4 text-center border border-indigo-200 rounded-lg bg-indigo-50">
        <div>
          <p className="text-sm text-gray-600">Record Count</p>
          <p className="text-2xl font-bold text-indigo-800">{recordCount}</p>
        </div>
        <div>
          <p className="text-sm text-gray-600">Total Value</p>
          <p className="text-2xl font-bold text-indigo-800">₹{totalValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
        </div>
      </div>

      {viewMode === 'card' ? (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {(filteredTransactions || []).map(txn => (
            <div key={txn.id} onClick={() => onSelectTransaction(txn.id)} className="p-4 space-y-3 transition-shadow bg-white border rounded-lg shadow-sm cursor-pointer hover:shadow-lg">
              <div className="flex items-start justify-between">
                <h2 className="text-lg font-bold text-gray-800">{txn.docNumber}</h2>
                <StatusBadge status={txn.status} mode={txn.mode} />
              </div>
              <p className="text-gray-600">{txn.partyName}</p>
              <p className="text-sm text-gray-500">{txn.docDate}</p>
              <p className="text-xl font-semibold text-right text-indigo-600">₹{txn.netTotal.toFixed(2)}</p>
            </div>
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto bg-white border rounded-lg shadow-sm">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 font-semibold text-left text-gray-600">Doc No</th>
                <th className="px-4 py-3 font-semibold text-left text-gray-600">Date</th>
                <th className="px-4 py-3 font-semibold text-left text-gray-600">Party</th>
                <th className="px-4 py-3 font-semibold text-right text-gray-600">Net Total</th>
                <th className="px-4 py-3 font-semibold text-center text-gray-600">Status</th>
              </tr>
            </thead>
            <tbody>
              {(filteredTransactions || []).map(txn => (
                <tr key={txn.id} onClick={() => onSelectTransaction(txn.id)} className="border-t cursor-pointer hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-800">{txn.docNumber}</td>
                  <td className="px-4 py-3 text-gray-600">{txn.docDate}</td>
                  <td className="px-4 py-3 text-gray-600">{txn.partyName}</td>
                  <td className="px-4 py-3 font-semibold text-right text-gray-800">₹{txn.netTotal.toFixed(2)}</td>
                  <td className="px-4 py-3 text-center"><StatusBadge status={txn.status} mode={txn.mode} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
